import { isBlankAggValue } from './tableAggregations';

/**
 * Parse display-formatted plain-number text back to a number, for the Excel
 * export — the plain-number twin of `parseCurrencyText` (./money) and
 * `parsePercentText` (./percent).
 *
 * Every export cell reaches the exporter as display TEXT, so a count, a weight or
 * a pro number landed in Excel as "number stored as text": it sorted as text,
 * SUM skipped it, and every cell wore Excel's green warning triangle. Returning a
 * number lets the exporter write a real numeric cell.
 *
 * Accepts what the grid renders: "42", "-5", "1,234", "12.50". `decimals` and
 * `grouped` (thousands separators) describe the text so the caller can pick a
 * number format that reads the same. Returns null, so the text stays text, for:
 *   - anything that is not wholly a number ("12 mi", "N/A", "70001-1234");
 *   - a leading zero ("007", "02134") — an identifier, and Excel would drop it;
 *   - more than 15 integer digits, which Excel cannot hold: it silently rounds
 *     the trailing digits of a long ID to zeros.
 *
 * Callers gate this on the COLUMN being numeric (`exportsAsNumber`): a digit
 * string in a text column — a zip, a booking number — stays text.
 */
export function parseNumberText(
  text: string,
): { value: number; decimals: number; grouped: boolean } | null {
  const s = text.trim();
  const m = /^([-+])?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) return null;
  const [, sign, intPart, frac] = m;
  const digits = intPart.replace(/,/g, '');
  if (digits.length > 1 && digits.startsWith('0')) return null;
  if (digits.length > 15) return null;
  const n = Number(digits + (frac ? `.${frac}` : ''));
  if (!Number.isFinite(n)) return null;
  return {
    value: sign === '-' ? -n : n,
    decimals: frac ? frac.length : 0,
    grouped: intPart.includes(','),
  };
}

/**
 * Excel number format that shows a parsed number the way the grid did:
 * "0" / "#,##0" for whole numbers, "0.00" / "#,##0.0" with the same fraction
 * digits otherwise. An explicit "0" rather than General, which flips anything
 * over 11 digits to scientific notation. Past 6 fraction digits it is a raw float
 * nobody formatted, so General shows it as-is.
 */
export function numberNumFmt(parsed: { decimals: number; grouped: boolean }): string {
  if (parsed.decimals > 6) return 'General';
  const base = parsed.grouped ? '#,##0' : '0';
  return parsed.decimals > 0 ? `${base}.${'0'.repeat(parsed.decimals)}` : base;
}

/**
 * Whether the Excel export writes a column's cells as numbers.
 *
 * A column is numeric when it is typed that way: `meta.numeric` (a Field Manager
 * number/decimal field, whose value can arrive as a string — routes declared
 * `-> Dict` serialize Decimal through Pydantic as "395.00"), or a declared
 * `meta.aggType` of number/money (the totals bar's type), or else the data: every
 * non-blank value arrived as a JSON number. A digit string from a text column —
 * zip, booking #, container — does not qualify, so it keeps its leading zeros.
 * A date column never qualifies.
 */
export function exportsAsNumber(values: unknown[], meta?: any): boolean {
  const declared = meta?.aggType;
  if (declared === 'number' || declared === 'money' || meta?.numeric) return true;
  if (declared || meta?.isDate) return false;
  let sawNumber = false;
  for (const value of values) {
    if (isBlankAggValue(value)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) return false;
    sawNumber = true;
  }
  return sawNumber;
}
